// src/sections/Profile.jsx — the user's own account (route /profile).
//
// ⭐ Replaces TWO avatar-menu modals (deleted in phase 4): the profile modal
// (name + locale) and the change-own-password modal, which the menu offered as
// "Profilo" and "Impostazioni" — the latter a mislabel, since it opened a
// password form and nothing else.
//
// ⛔ THE LOCALE CONTROL IS NOT HERE. It moved to the avatar menu's IT/EN
// switcher (phase 4), which now writes BOTH the UI language and `users.locale`.
// ⚠️ `users.locale` is the language of every transactional email — it is not a
// leftover and must never simply be dropped.
//
// ⭐ The password form reuses auth/passwordPolicy.js, the same rules the
// forgot-password screen enforces. ⚠️ UX ONLY: the backend validates 8–128
// characters and nothing about character classes, so this makes the product
// stop contradicting itself — it does not make anything safer.
import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, X, UserRound } from 'lucide-react';
import SectionCard, { PAGE_COLUMN, CARD_BODY } from '../components/ui/SectionCard.jsx';
import { useAuth } from '../auth/AuthContext.jsx';
import { updateMe, changeOwnPassword } from '../admin/adminApi.js';
import { checkPassword, isPasswordValid, PASSWORD_RULES } from '../auth/passwordPolicy.js';
import { authFallbackCopy } from '../auth/authErrorCopy.js';
import { COLORS, FONT_SANS, RADII, SEMANTIC } from '../constants/theme.js';

const inputStyle = {
  width: '100%', padding: '10px 14px', borderRadius: RADII.controlInner,
  border: `1px solid ${COLORS.borderStrong}`, background: COLORS.surface,
  color: COLORS.ink, fontSize: '14px', boxSizing: 'border-box',
  outline: 'none', fontFamily: FONT_SANS,
};
const labelStyle = {
  display: 'block', color: COLORS.textSecondary, fontSize: '13px',
  marginBottom: '6px', fontWeight: 500,
};
// Each form is a panel INSIDE the page's section card (2026-09-27, the
// Gestione Workspace look); the two sit side by side when there is room.
const cardStyle = {
  background: COLORS.surface, border: `1px solid ${COLORS.border}`,
  borderRadius: RADII.control, padding: '20px 22px', minWidth: 0,
};
const formsGrid = {
  ...CARD_BODY, display: 'grid', gap: '20px', alignItems: 'start',
  gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
};

function normalizeName(s) {
  const trimmed = (s || '').trim();
  return trimmed === '' ? null : trimmed;
}

export default function Profile() {
  const { t } = useTranslation('admin');
  const { user, setUser, isSuperadmin, adminMode } = useAuth();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState('');
  const [nameSaved, setNameSaved] = useState(false);

  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [savingPw, setSavingPw] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSaved, setPwSaved] = useState(false);

  useEffect(() => {
    setFirstName(user?.first_name || '');
    setLastName(user?.last_name || '');
  }, [user]);

  // Only CHANGED keys — an untouched empty field sent as null would wipe data.
  const patch = {};
  const nextFirst = normalizeName(firstName);
  const nextLast = normalizeName(lastName);
  if (nextFirst !== (user?.first_name ?? null)) patch.first_name = nextFirst;
  if (nextLast !== (user?.last_name ?? null)) patch.last_name = nextLast;
  const nameDirty = Object.keys(patch).length > 0;

  const rules = checkPassword(newPw);
  const matches = newPw.length > 0 && newPw === confirmPw;
  const canSubmitPw = !!currentPw && isPasswordValid(newPw) && matches && !savingPw;

  async function saveName(e) {
    e.preventDefault();
    if (!nameDirty) return;
    setNameError(''); setSavingName(true);
    try {
      setUser(await updateMe(patch));
      setNameSaved(true);
      setTimeout(() => setNameSaved(false), 2000);
    } catch (err) {
      // ⛔ Never err.message (server English) — authErrorCopy.js words it.
      setNameError(authFallbackCopy(t, err, 'errors:auth.nameSaveFailed'));
    } finally { setSavingName(false); }
  }

  async function savePassword(e) {
    e.preventDefault();
    if (!canSubmitPw) return;
    setPwError(''); setSavingPw(true);
    try {
      await changeOwnPassword(currentPw, newPw);
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
      setPwSaved(true);
      setTimeout(() => setPwSaved(false), 2000);
    } catch (err) {
      setPwError(err?.code === 'AUTH_INVALID_CURRENT_PASSWORD'
        ? t('errors:auth.wrongCurrentPassword')
        : authFallbackCopy(t, err, 'errors:auth.passwordChangeFailed'));
    } finally { setSavingPw(false); }
  }

  if (isSuperadmin && adminMode) {
    // Consistent with every other member section.
    return <Navigate to="/admin/users" replace />;
  }

  return (
    <div style={PAGE_COLUMN}>
      <SectionCard icon={UserRound} headingLevel={1} title={t('profile.title')} subtitle={user?.email}>
      <div style={formsGrid}>

      <form style={cardStyle} onSubmit={saveName}>
        <h2 style={{ fontSize: '15px', fontWeight: 600, margin: '0 0 16px' }}>{t('profile.identity')}</h2>
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle} htmlFor="pf-first">{t('profile.firstName')}</label>
          <input id="pf-first" type="text" value={firstName} maxLength={100}
                 onChange={e => setFirstName(e.target.value)} style={inputStyle} />
        </div>
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle} htmlFor="pf-last">{t('profile.lastName')}</label>
          <input id="pf-last" type="text" value={lastName} maxLength={100}
                 onChange={e => setLastName(e.target.value)} style={inputStyle} />
        </div>
        {nameError && <Notice tone="error">{nameError}</Notice>}
        {nameSaved && <Notice tone="ok">{t('profile.saved')}</Notice>}
        <SubmitButton disabled={savingName || !nameDirty}>
          {savingName ? t('profile.saving') : t('common:actions.save')}
        </SubmitButton>
      </form>

      <form style={cardStyle} onSubmit={savePassword}>
        <h2 style={{ fontSize: '15px', fontWeight: 600, margin: '0 0 16px' }}>{t('changePassword.title')}</h2>
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle} htmlFor="pf-cur">{t('changePassword.currentPassword')}</label>
          <input id="pf-cur" type="password" value={currentPw} autoComplete="current-password"
                 onChange={e => setCurrentPw(e.target.value)} style={inputStyle} />
        </div>
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle} htmlFor="pf-new">{t('changePassword.newPassword')}</label>
          <input id="pf-new" type="password" value={newPw} autoComplete="new-password"
                 onChange={e => setNewPw(e.target.value)} style={inputStyle} />
        </div>
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle} htmlFor="pf-conf">{t('changePassword.confirmPassword')}</label>
          <input id="pf-conf" type="password" value={confirmPw} autoComplete="new-password"
                 onChange={e => setConfirmPw(e.target.value)} style={inputStyle} />
        </div>

        {/* ⚠️ UX only — the backend enforces 8–128 characters and nothing else.
            Same rules and same keys as the forgot-password screen, so the two
            doors into a password finally agree. */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ ...labelStyle, marginBottom: '10px' }}>
            {t('auth:passwordForm.requirementsTitle')}
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '6px' }}>
            {PASSWORD_RULES.map((rule) => {
              const ok = rules[rule];
              const key = `req${rule.charAt(0).toUpperCase()}${rule.slice(1)}`;
              return (
                <li key={rule} data-testid={`rule-${rule}`} data-ok={ok ? 'true' : 'false'}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px',
                             color: ok ? SEMANTIC.verde.fg : COLORS.textSecondary }}>
                  {ok ? <Check size={14} aria-hidden /> : <X size={14} aria-hidden />}
                  {t(`auth:passwordForm.${key}`)}
                </li>
              );
            })}
          </ul>
        </div>

        {confirmPw.length > 0 && !matches && <Notice tone="error">{t('changePassword.passwordMismatch')}</Notice>}
        {pwError && <Notice tone="error">{pwError}</Notice>}
        {pwSaved && <Notice tone="ok">{t('changePassword.saved')}</Notice>}
        <SubmitButton disabled={!canSubmitPw}>
          {savingPw ? t('changePassword.updating') : t('changePassword.submit')}
        </SubmitButton>
      </form>
      </div>
      </SectionCard>
    </div>
  );
}

function Notice({ tone, children }) {
  const sem = tone === 'ok' ? SEMANTIC.verde : SEMANTIC.errore;
  return (
    <div style={{ marginBottom: '16px', padding: '10px 14px', borderRadius: RADII.controlInner,
                  background: sem.bg, color: sem.fg, fontSize: '13px' }}>{children}</div>
  );
}

function SubmitButton({ disabled, children }) {
  return (
    <button type="submit" disabled={disabled}
            style={{ width: '100%', padding: '10px', borderRadius: RADII.controlInner, border: 'none',
                     background: disabled ? COLORS.borderStrong : COLORS.accent, color: COLORS.surface,
                     fontSize: '14px', fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer' }}>
      {children}
    </button>
  );
}
